import { Requisition, RequisitionFulfillmentItem } from '@kalos/kalos-rpc';
import { toast } from '@kalos/ui';
import { useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import { type ComponentProps, useMemo, useState } from 'react';

import { useAuth } from '../../../../context/AuthContext';
import { useEmailClientSendMutation } from '../../../../hooks/react-query/useEmailClientQuery';
import { getFileQueryConfig } from '../../../../hooks/react-query/useFileQuery';
import { useRequisitionUpdateMutation } from '../../../../hooks/react-query/useToolFundQuery';
import { useUserQuery } from '../../../../hooks/react-query/useUserQuery';
import { FileClientService, InventoryClientService, timestamp } from '../../../../tools/helpers';
import { getFieldMaskFromDirtyField } from '../../EmployeeDepartments/utils';
import { type UpdateStatusForm } from './UpdateStatusForm';

// Helper function to parse comma-delimited file IDs
const parseFileIds = (fileIdString: string | undefined): number[] => {
  if (!fileIdString) return [];
  return fileIdString
    .split(',')
    .map((id) => parseInt(id.trim()))
    .filter((id) => !isNaN(id));
};

/**
 * What desktop's Update Status dialog does with the form (RequisitionStatusDialog): the tool line
 * it's about, the fulfillment update sent straight to InventoryClientService, the request status
 * roll-up and the approved or rejected email. Desktop's dialog and the phone's Update status page
 * both run this, so the calls, their order and the email are the same on both.
 */
export function useRequisitionStatusUpdate({
  onClose,
  requisition,
  requisitionItem,
  fulfillmentItem,
  onSuccessUpdate,
}: {
  onClose: () => void;
  onSuccessUpdate: () => void;
  requisition: Requisition;
  requisitionItem?: Requisition['items'][0];
  fulfillmentItem?: RequisitionFulfillmentItem;
}) {
  const { user: currentUser } = useAuth();
  const updateRequisition = useRequisitionUpdateMutation();
  const emailClientSend = useEmailClientSendMutation();
  const userQuery = useUserQuery({
    filters: { id: requisition.userId },
  });
  const queryClient = useQueryClient();

  const [isUpdating, setIsUpdating] = useState(false);

  // Create a modified requisition object for the specific item if requisitionItem is provided
  const statusRequest = useMemo(() => {
    if (requisitionItem && fulfillmentItem) {
      // Return a modified requisition with the fulfillment item's status
      return {
        ...requisition,
        statusId: fulfillmentItem.statusId || requisitionItem.status,
        fulfillments: [fulfillmentItem],
        items: [requisitionItem],
      };
    } else if (requisitionItem) {
      // Fallback: Find the fulfillment for this specific item
      const itemFulfillment = requisition.fulfillments?.find(
        (fulfillment) => fulfillment.requisitionItemId === requisitionItem.id,
      );

      // Return a modified requisition with the item's status and fulfillment
      return {
        ...requisition,
        statusId: requisitionItem.status,
        fulfillments: itemFulfillment ? [itemFulfillment] : [],
        items: [requisitionItem],
      };
    }
    return requisition;
  }, [requisition, requisitionItem, fulfillmentItem]);

  const handleSubmit: ComponentProps<typeof UpdateStatusForm>['onSubmit'] = async ({
    data: formData,
    dirtyFields,
  }) => {
    try {
      setIsUpdating(true);
      const fieldMask = getFieldMaskFromDirtyField(dirtyFields);

      if (!fieldMask.includes('UpdatedAt')) {
        fieldMask.push('UpdatedAt');
      }

      let receiptFileId: number | undefined;
      let photoFileId: number | undefined;

      // Handle receipt upload or selection
      if (formData.receipt instanceof File) {
        const receiptFileName = `${requisition.id}_receipt_${Date.now()}_${formData.receipt.name}`;

        const fileRes = await FileClientService.uploadFileToS3AndCreateFile({
          file: formData.receipt,
          fileKey: receiptFileName,
          bucket: 'kalos-tools-files',
        });
        receiptFileId = fileRes.id;
        fieldMask.push('FileIds');
      } else if (
        formData.selectedReceiptId &&
        (dirtyFields.selectedReceiptId ||
          !fulfillmentItem?.fileIds.includes(formData.selectedReceiptId.toString()))
      ) {
        // Use selected existing receipt if user changed selection OR fulfillment doesn't have a receipt yet
        receiptFileId = formData.selectedReceiptId;
        fieldMask.push('FileIds');
      }

      const fulfillmentFieldMask: string[] = [];
      const currentFulfillment = requisition.fulfillments?.find(
        (f) => f.id === (fulfillmentItem?.id || requisition.fulfillments[0].id),
      );
      const fulfillmentData: Partial<RequisitionFulfillmentItem> = {
        ...currentFulfillment,
        id: fulfillmentItem?.id || requisition.fulfillments[0].id,
      };

      // Handle photo upload and/or status update for fulfillment item
      // Determine if we need to update fulfillment data
      const needsUpdateFulfillment =
        formData.statusId === 2 ||
        formData.statusId === 3 ||
        dirtyFields.picture ||
        dirtyFields.notes ||
        dirtyFields.actualCost ||
        dirtyFields.assetTagId ||
        dirtyFields.fulfillmentDate ||
        dirtyFields.statusId; // Always update fulfillment when status changes

      if (needsUpdateFulfillment) {
        if (receiptFileId) {
          // Get all file IDs from requisition to check which are receipts
          const requisitionFileIds = parseFileIds(requisition.fileIds);

          // Get existing fulfillment fileIds
          const existingFileIds = currentFulfillment?.fileIds
            ? currentFulfillment.fileIds
                .split(',')
                .map((id) => id.trim())
                .filter((id) => id)
            : [];

          // Filter out any existing receipt fileIds
          // Receipts are files that are in the requisition's fileId list (and not photos)
          const nonReceiptFileIds = existingFileIds.filter((fileId) => {
            return !requisitionFileIds.includes(parseInt(fileId));
          });

          // Add the new receipt fileId
          const newFileIdString = [...nonReceiptFileIds, receiptFileId.toString()].join(',');
          fulfillmentData.fileIds = newFileIdString;
          fulfillmentFieldMask.push('FileIds');
        }

        // Handle status updates for fulfillment item
        if (dirtyFields.statusId) {
          // Update fulfillment item statusId to match requisition item status
          fulfillmentData.statusId = formData.statusId;
          fulfillmentFieldMask.push('status_id');

          // Always include updatedAt
          fulfillmentData.updatedAt = format(new Date(), 'yyyy-MM-dd HH:mm:ss');
          fulfillmentFieldMask.push('UpdatedAt');
        }

        // Handle default updates for status = 2 (approval)
        if (formData.statusId === 2) {
          // Set who fulfilled/processed this request
          if (dirtyFields.statusId && requisition.statusId !== 2) {
            fulfillmentData.fulfilledByUserId = currentUser.id;
            fulfillmentFieldMask.push('FulfilledByUserId');
          }

          // Handle fulfillment date - always update when purchase date changes
          const currentFulfillmentDate = currentFulfillment?.fulfillmentDate;
          const newFulfillmentDate = timestamp(false, formData.fulfillmentDate);

          if (dirtyFields.fulfillmentDate || currentFulfillmentDate !== newFulfillmentDate) {
            if (formData.fulfillmentDate) {
              const formattedDate = timestamp(true, formData.fulfillmentDate);
              fulfillmentData.fulfillmentDate = formattedDate;
              // Set updatedAt to match fulfillmentDate for visual synchronization
              fulfillmentData.updatedAt = format(new Date(), 'yyyy-MM-dd HH:mm:ss');
              fulfillmentFieldMask.push('FulfillmentDate');
              fulfillmentFieldMask.push('UpdatedAt');
            }
          }

          // Handle notes
          if (dirtyFields.notes) {
            fulfillmentData.notes = formData.notes;
            fulfillmentFieldMask.push('Notes');
          }

          // Handle actual cost
          if (
            dirtyFields.actualCost &&
            formData.actualCost !== undefined &&
            formData.actualCost > 0
          ) {
            fulfillmentData.actualCost = formData.actualCost;
            fulfillmentFieldMask.push('ActualCost');
          }

          // Handle asset tag
          if (dirtyFields.assetTagId) {
            fulfillmentData.assetTag = formData.assetTagId;
            fulfillmentFieldMask.push('AssetTag');
          }
        } else {
          // For other statuses, respect dirtyFields
          if (dirtyFields.statusId) {
            fulfillmentData.updatedAt = format(new Date(), 'yyyy-MM-dd HH:mm:ss');
            fulfillmentFieldMask.push('UpdatedAt');

            // Set who processed this request for rejections (status = 3)
            if (formData.statusId === 3) {
              fulfillmentData.fulfilledByUserId = currentUser.id;
              fulfillmentFieldMask.push('FulfilledByUserId');

              // Handle rejection reason - append to notes
              if (dirtyFields.reason && formData.reason) {
                const currentNotes = fulfillmentData.notes || currentFulfillment?.notes || '';
                const rejectionNote = `${currentNotes !== '' ? '\n' : ''}Rejection Reason: ${formData.reason}`;
                fulfillmentData.notes = currentNotes + rejectionNote;
                fulfillmentFieldMask.push('Notes');
              }
            }
          }

          // Handle notes independently
          if (dirtyFields.notes) {
            fulfillmentData.notes = formData.notes;
            fulfillmentFieldMask.push('Notes');
          }

          if (
            dirtyFields.actualCost &&
            formData.actualCost !== undefined &&
            formData.actualCost > 0
          ) {
            fulfillmentData.actualCost = formData.actualCost;
            fulfillmentFieldMask.push('ActualCost');
          }

          if (dirtyFields.assetTagId) {
            fulfillmentData.assetTag = formData.assetTagId;
            fulfillmentFieldMask.push('AssetTag');
          }
        }

        const req = RequisitionFulfillmentItem.create({
          ...fulfillmentData,
          fieldMask: fulfillmentFieldMask,
        });

        await InventoryClientService.UpdateRequistionFulfillmentItem(req);
        if (photoFileId) {
          queryClient.invalidateQueries({
            queryKey: getFileQueryConfig({ filter: { id: photoFileId } }).queryKey,
          });
        }

        // Update requisition status based on fulfillment items
        if (dirtyFields.statusId) {
          // Get updated fulfillment items to check overall status
          const updatedFulfillments =
            requisition.fulfillments?.map((f) =>
              f.id === fulfillmentData.id ? { ...f, statusId: formData.statusId } : f,
            ) || [];

          // Check if all fulfillment items are approved (2) or rejected (3)
          const allFulfillmentsComplete = updatedFulfillments.every(
            (f) => f.statusId === 2 || f.statusId === 3,
          );

          // Check if any fulfillments have been processed
          const anyFulfillmentsProcessed = updatedFulfillments.some(
            (f) => f.statusId === 2 || f.statusId === 3,
          );

          // Determine requisition status: 1 = incomplete, 3 = in progress, 2 = complete
          let newRequisitionStatus: number;
          if (allFulfillmentsComplete) {
            newRequisitionStatus = 2; // Complete - all fulfillments processed
          } else if (anyFulfillmentsProcessed) {
            newRequisitionStatus = 3; // In Progress - at least one processed, but not all
          } else {
            newRequisitionStatus = 1; // Incomplete - all pending
          }

          // Update requisition status only - include all required fields
          const updatedRequisition = Requisition.create({
            ...requisition,
            id: requisition.id,
            statusId: newRequisitionStatus,
            fieldMask: ['StatusId'],
            updatedAt: format(new Date(), 'yyyy-MM-dd HH:mm:ss'),
          });

          await updateRequisition.mutateAsync(updatedRequisition);
        }
      }

      // Update requisition item status if requisitionItem is provided (but not if we already updated fulfillment)
      if (requisitionItem && dirtyFields.statusId && !fulfillmentItem) {
        const updatedItems = requisition.items.map((item) =>
          item.id === requisitionItem.id ? { ...item, status: formData.statusId } : item,
        );

        const updatedRequisition = Requisition.create({
          ...requisition,
          items: updatedItems,
          purchaseOrder: formData.purchaseOrder,
          id: requisition.id,
          fieldMask: [...fieldMask, 'Items'],
          updatedAt: format(new Date(), 'yyyy-MM-dd HH:mm:ss'),
        });

        await updateRequisition.mutateAsync(updatedRequisition);
      } else if (!fulfillmentItem) {
        // Fallback to updating requisition status if no specific item and no fulfillment item
        const updatedRequisition = Requisition.create({
          ...requisition,
          statusId: formData.statusId,
          purchaseOrder: formData.purchaseOrder,
          id: requisition.id,
          fieldMask,
          updatedAt: format(new Date(), 'yyyy-MM-dd HH:mm:ss'),
        });

        await updateRequisition.mutateAsync(updatedRequisition);
      }

      // Query invalidation is now handled by the mutation's onSuccess handler

      // Call onSuccessUpdate to refresh the parent component
      onSuccessUpdate();

      // Close the status dialog after successful update
      onClose();
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Failed to update requisition',
        description: 'Please try again later',
      });
      console.error('Failed to update requisition:', error);
      return;
    } finally {
      setIsUpdating(false);
    }

    // Send email notification
    try {
      if (userQuery.isSuccess && userQuery.data) {
        const emailTemplate = getRequisitionStatusEmail({
          statusId: formData.statusId,
          purchaseOrder: formData.purchaseOrder || '',
          rejectReason: formData.reason || '',
          purchaseDate: timestamp(true, formData.fulfillmentDate),
          actualDeliveryLocation: requisition.fulfillments?.[0].actualDeliveryLocation,
        });

        await emailClientSend.mutateAsync({
          subject: `Tool Fund Request ${formData.statusId === 2 ? 'Approved' : 'Rejected'}`,
          body: emailTemplate,
          recipient: userQuery.data?.email,
        });
      }
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Failed to send notification email to the requester',
        description: 'Please try again later',
      });
      console.error('Failed to send notification email to the requester:', error);
    } finally {
      setIsUpdating(false);
    }
  };

  return { statusRequest, handleSubmit, isUpdating };
}

export const getRequisitionStatusEmail = ({
  statusId,
  purchaseOrder,
  rejectReason,
  purchaseDate,
  actualDeliveryLocation,
}: {
  statusId: number;
  purchaseOrder: string;
  rejectReason: string;
  purchaseDate: string;
  actualDeliveryLocation?: string;
}) => {
  if (statusId === 2) {
    // Approved
    return `
      <div style="font-family: Arial, sans-serif; padding: 20px;">
        <h2>Your Tool Fund Request has been Approved!</h2>
        <p>Good news! Your tool fund request has been approved.</p>
        <div style="margin: 20px 0; padding: 15px; background-color: #f8f9fa; border-radius: 4px;">
          <h3 style="margin-top: 0;">Details:</h3>
          <ul>
            ${purchaseOrder ? `<li>Purchase Order: ${purchaseOrder}</li>` : ''}
            <li>Purchase Date: ${purchaseDate}</li>
            ${actualDeliveryLocation ? `<li>Delivery Method: ${actualDeliveryLocation}</li>` : ''}
          </ul>
        </div>
        <p>Please keep an eye on your email for further updates regarding delivery.</p>
      </div>
    `;
  } else {
    // Rejected
    return `
      <div style="font-family: Arial, sans-serif; padding: 20px;">
        <h2>Tool Fund Request Status Update</h2>
        <p>Your tool fund request has been reviewed and cannot be approved at this time.</p>
        <div style="margin: 20px 0; padding: 15px; background-color: #f8f9fa; border-radius: 4px;">
        <h3 style="margin-top: 0;">Reject Reason: ${rejectReason}</h3>
          <h3 style="margin-top: 0;">Next Steps:</h3>
          <p>Please reach out to your manager for more information about this decision and to discuss next steps.</p>
        </div>
      </div>
    `;
  }
};
